import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'
import type { Document } from '@contentful/rich-text-types'
import { BLOCKS } from '@contentful/rich-text-types'
import styled from '@emotion/styled'
import type { FC, PropsWithChildren } from 'react'
import { useMemo } from 'react'

import RichText, { ColoredTypography } from '@segment/matcha/recipes/RichText'

import ContentDownloadForm from '@segment/templates/Forms/ContentDownloadForm'

import type { TableOfContentState } from '@segment/utils/useRichTextTableOfContent'

import PropCardCta from '../PropCardCta'

interface ContentProps {
  data: Document
  tableOfContentState: TableOfContentState
}
const Content: FC<PropsWithChildren<ContentProps>> = ({ data, tableOfContentState }) => {
  const options = useMemo((): RichTextRenderOptions => {
    return {
      renderNode: {
        [BLOCKS.HEADING_2](node, children) {
          const id = tableOfContentState.getId(node)
          return (
            <ColoredTypography variant="h2" tag="h2" id={id}>
              {children}
            </ColoredTypography>
          )
        },
        [BLOCKS.EMBEDDED_ENTRY](node) {
          switch (node.data.target.sys.contentType.sys.id) {
            case `inlineCta`: {
              const { heading, paragraph, buttonText, buttonUrl, image } = node.data.target.fields
              return (
                <PropCardCta
                  heading={heading}
                  paragraph={paragraph}
                  buttonText={buttonText}
                  buttonUrl={buttonUrl}
                  image={image}
                />
              )
            }

            case `downloadOnSubmitForm`: {
              const { header, description, image, downloadableAssetUrl, formId } = node.data.target.fields
              return (
                <ContentDownloadForm
                  heading={header}
                  description={description}
                  image={image?.fields?.file?.url}
                  assetUrl={downloadableAssetUrl}
                  customFormIdentifier={formId}
                />
              )
            }

            default:
              return null
          }
        },
      },
    }
  }, [tableOfContentState])

  const content = useMemo(() => {
    if (!data) {
      return
    }

    return <RichText data={data} options={options} />
  }, [data, options])

  return <Wrapper>{content}</Wrapper>
}

export default Content

const Wrapper = styled.div`
  margin: auto;
`
